import React from 'react';
import PropTypes from 'prop-types';

import { Box } from '../../design/css';
import OrderButton from './orderButton';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { orderStatus as statusConstants } from '../../utils/constant';
import { FieldDate } from '../../form/fields';

const OrderAction = ({
  button,
  orderStatus,
  status,
  onStatusChange,
  isOrderValidating,
  isValidated,
  actionChanged,
  whiteGloveRequired,
  wgInstallationDate,
  setWgInstallationDate,
  isOdysseyValues,
  isThisOdysseyAI
}) => {
  const localizedString = useLocalizedStringsContext();
  const {
    ORDERS: {
      orderAction,
      save,
      sendContract,
      validate,
      defaultOption,
      wgInstallationFieldLabel,
      wgInstallationFieldPlaceholder
    },
    contentfulValues: { yes }
  } = localizedString;
  const {
    pendingValidation,
    waitingOnSales,
    waitingOnCredit,
    contractSigned,
    contractSent,
    validated,
    withConcierge,
    implementationInProgress,
    waitingOnCompletionCall,
    orderInstalled
  } = statusConstants;

  const showWgDateField =
    actionChanged &&
    status === implementationInProgress &&
    whiteGloveRequired === yes;

  const isReturnToSalesAllowed = [
    pendingValidation,
    waitingOnCredit,
    contractSent
  ].includes(orderStatus);
  const isWaitingOnCreditAllowed = [pendingValidation].includes(orderStatus);
  const isSendContractAllowed = [
    pendingValidation,
    waitingOnCredit,
    contractSent
  ].includes(orderStatus);
  const isValidateAllowed = [
    pendingValidation,
    waitingOnCredit,
    contractSent,
    contractSigned
  ].includes(orderStatus);
  const showFullfillmentStatuses = [
    validated,
    withConcierge,
    implementationInProgress,
    waitingOnCompletionCall
  ].includes(orderStatus);
  const isWithConcierge = [
    withConcierge,
    implementationInProgress,
    waitingOnCompletionCall,
    orderInstalled
  ].includes(orderStatus);
  const isWaitingOnCompletionCall = [
    waitingOnCompletionCall,
    orderInstalled
  ].includes(orderStatus);
  const isOrderInstalled = orderStatus === orderInstalled;

  const getButtonLabel = () => {
    if (actionChanged) {
      switch (status) {
        case contractSent:
          return sendContract;
        case validated:
          return validate;
        default:
          return save;
      }
    }
    return save;
  };

  return (
    <>
      <div
        className="frg-select-container color-light"
        style={{
          position: 'relative',
          width: '100%',
          height: '50px',
          marginTop: '1.5rem'
        }}
      >
        <select style={{ fontSize: '1rem' }} onChange={onStatusChange}>
          <option selected disabled>
            {defaultOption}
          </option>
          {isReturnToSalesAllowed && (
            <option value={waitingOnSales}>{orderAction.returnToSales}</option>
          )}
          {isWaitingOnCreditAllowed &&
            (['No', 'Non'].includes(isOdysseyValues) || !isOdysseyValues) &&
            (['No', 'Non'].includes(isThisOdysseyAI) || !isThisOdysseyAI) && (
              <option value={waitingOnCredit}>
                {orderAction.waitingOnCredit}
              </option>
            )}
          {isSendContractAllowed &&
            (['No', 'Non'].includes(isOdysseyValues) || !isOdysseyValues) &&
            (['No', 'Non'].includes(isThisOdysseyAI) || !isThisOdysseyAI) && (
              <option value={contractSent}>{orderAction.sendContract}</option>
            )}
          {isValidateAllowed && (
            <option value={validated}>{orderAction.validate}</option>
          )}
          {showFullfillmentStatuses && (
            <>
              {!isWithConcierge && (
                <option value={withConcierge}>
                  {orderAction.withConcierge}
                </option>
              )}
              {
                <option value={implementationInProgress}>
                  {orderAction.implementationInProgress}
                </option>
              }

              {!isWaitingOnCompletionCall && (
                <option value={waitingOnCompletionCall}>
                  {orderAction.waitingOnCompletionCall}
                </option>
              )}

              <option value={orderInstalled}>
                {orderAction.orderInstalled}
              </option>
            </>
          )}
        </select>
      </div>
      {showWgDateField && (
        <div style={{ marginTop: '1rem' }}>
          <FieldDate
            data={{
              value: wgInstallationDate,
              placeholder: wgInstallationFieldPlaceholder,
              label: wgInstallationFieldLabel,
              onChange: date => {
                setWgInstallationDate(date.target.value);
              },
              name: 'wgInstallationDate',
              required: false,
              fieldDateConfig: '',
              readonly: false,
              tooltip: ''
            }}
          />
        </div>
      )}

      <Box
        display="flex"
        justifyContent="space-evenly"
        flexDirection="row"
        width={100}
        mdWidth={100}
        right={3}
        left={3}
        top={5}
        bottom={4}
      >
        {!isOrderInstalled && (
          <OrderButton
            button={{ ...button, label: getButtonLabel() }}
            isOrderValidating={isOrderValidating}
            isValidated={isValidated}
            actionChanged={actionChanged}
          />
        )}
      </Box>
    </>
  );
};

OrderAction.propTypes = {
  button: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    variant: PropTypes.string,
    isVisibleOnValidate: PropTypes.bool
  }).isRequired,
  orderStatus: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  isOrderValidating: PropTypes.bool.isRequired,
  isValidated: PropTypes.bool.isRequired,
  actionChanged: PropTypes.bool.isRequired,
  whiteGloveRequired: PropTypes.string.isRequired,
  wgInstallationDate: PropTypes.string.isRequired,
  setWgInstallationDate: PropTypes.func.isRequired,
  isOdysseyValues: PropTypes.string.isRequired,
  isThisOdysseyAI: PropTypes.string.isRequired
};

export default OrderAction;
